import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { Permission } from '@workforce/shared';
import { useAuth } from '@/lib/auth';
import { Layout } from '@/components/Layout';
import { Spinner } from '@/components/ui';
import { t } from '@/lib/i18n';
import { LoginPage } from '@/pages/Login';
import { ForcePasswordChangePage } from '@/pages/ForcePasswordChange';
import { DashboardPage } from '@/pages/Dashboard';
import { EmployeesPage } from '@/pages/Employees';
import { EmployeeDetailPage } from '@/pages/EmployeeDetail';
import { EmployeeFormPage } from '@/pages/EmployeeForm';
import { DepartmentsPage } from '@/pages/Departments';
import { SchedulesPage } from '@/pages/Schedules';
import { HolidaysPage } from '@/pages/Holidays';
import { SettingsPage } from '@/pages/Settings';
import { AuditLogPage } from '@/pages/AuditLog';
import { ProfilePage } from '@/pages/Profile';
import { ComingSoonPage, NotFoundPage } from '@/pages/ComingSoon';
import { MyLeavePage } from '@/pages/MyLeave';
import { MyBalancePage } from '@/pages/MyBalance';
import { LeaveAdminPage } from '@/pages/LeaveAdmin';
import { BalancesPage } from '@/pages/Balances';
import { LeaveSetupPage } from '@/pages/LeaveSetup';
import { MyAttendancePage } from '@/pages/MyAttendance';
import { AttendanceAdminPage } from '@/pages/AttendanceAdmin';
import { ScanPage } from '@/pages/Scan';
import { KioskPage } from '@/pages/Kiosk';
import { ReportsPage } from '@/pages/Reports';
import { PayrollPage } from '@/pages/Payroll';
import { ProductivityPage } from '@/pages/Productivity';
import { MyProductivityPage } from '@/pages/MyProductivity';
import { ProjectDetailPage, ProjectsPage } from '@/pages/Projects';
import { ProjectHoursPage } from '@/pages/ProjectHours';
import { PolicyPage } from '@/pages/Policy';

/** UI-level guard. The API enforces the same permissions independently. */
function RequirePerm({ perm, children }: { perm: Permission; children: ReactNode }) {
  const { can } = useAuth();
  return can(perm) ? <>{children}</> : <NotFoundPage />;
}

export function App() {
  const { user, loading } = useAuth();
  // The door tablet runs without a user login (it is paired with a device key).
  if (window.location.pathname === '/kiosk') return <KioskPage />;
  if (loading) return <Spinner label={t('Starting…')} />;
  if (!user) return <LoginPage />;
  if (user.mustChangePassword) return <ForcePasswordChangePage />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<DashboardPage />} />
        <Route path="employees" element={<RequirePerm perm="employees.read"><EmployeesPage /></RequirePerm>} />
        <Route path="employees/new" element={<RequirePerm perm="employees.write"><EmployeeFormPage /></RequirePerm>} />
        <Route path="employees/:id" element={<RequirePerm perm="employees.read"><EmployeeDetailPage /></RequirePerm>} />
        <Route path="employees/:id/edit" element={<RequirePerm perm="employees.write"><EmployeeFormPage /></RequirePerm>} />
        <Route path="departments" element={<RequirePerm perm="departments.write"><DepartmentsPage /></RequirePerm>} />
        <Route path="schedules" element={<RequirePerm perm="schedules.write"><SchedulesPage /></RequirePerm>} />
        <Route path="holidays" element={<RequirePerm perm="holidays.write"><HolidaysPage /></RequirePerm>} />
        <Route path="settings" element={<RequirePerm perm="settings.write"><SettingsPage /></RequirePerm>} />
        <Route path="projects" element={<RequirePerm perm="projects.read"><ProjectsPage /></RequirePerm>} />
        <Route path="projects/:id" element={<RequirePerm perm="projects.read"><ProjectDetailPage /></RequirePerm>} />
        <Route path="project-hours" element={<RequirePerm perm="projects.read"><ProjectHoursPage /></RequirePerm>} />
        <Route path="policy" element={<PolicyPage />} />
        <Route path="audit" element={<RequirePerm perm="audit.read"><AuditLogPage /></RequirePerm>} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="attendance" element={<RequirePerm perm="attendance.read"><AttendanceAdminPage /></RequirePerm>} />
        <Route path="leave" element={<RequirePerm perm="leave.read"><LeaveAdminPage /></RequirePerm>} />
        <Route path="leave/setup" element={<RequirePerm perm="leave.types.write"><LeaveSetupPage /></RequirePerm>} />
        <Route path="balances" element={<RequirePerm perm="leave.read"><BalancesPage /></RequirePerm>} />
        <Route path="productivity" element={<RequirePerm perm="productivity.read"><ProductivityPage /></RequirePerm>} />
        <Route path="payroll" element={<RequirePerm perm="payroll.manage"><PayrollPage /></RequirePerm>} />
        <Route path="reports" element={<RequirePerm perm="reports.read"><ReportsPage /></RequirePerm>} />
        <Route path="me/attendance" element={<MyAttendancePage />} />
        <Route path="scan" element={<ScanPage />} />
        <Route path="me/leave" element={<MyLeavePage />} />
        <Route path="me/balance" element={<MyBalancePage />} />
        <Route path="me/productivity" element={<MyProductivityPage />} />
        <Route path="login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
